import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { useSettings } from "../state/settings";
import { useWorkspaces } from "../state/workspaces";
import { useUi } from "../state/ui";
import { texte } from "../texte";
import { toast } from "../notify";
import * as api from "../api/client";
import type { ApiCtx } from "../api/client";
import type { CloudConfig } from "../api/types";
import { uploadInBatches } from "../convert/chunker";
import { uploadLargeFile } from "../files/upload";
import { convLabelFor, convertFilesForUpload, normalizeUmlauts } from "../files/convert";
import { ConfigEditor } from "./parts/ConfigEditor";
import { GoalEditor } from "./parts/GoalEditor";

function parseConfigJs(content: string): any {
  const start = content.indexOf("{");
  const end = content.lastIndexOf("}");
  if (start !== -1 && end !== -1) {
    try {
      return JSON.parse(content.substring(start, end + 1));
    } catch (e) {
      console.error("Failed to parse config.js content as JSON", e);
    }
  }
  return null;
}

type PendingFile = {
  id: string;
  name: string;
  convLabel: string;
  file: File;
  isZip?: boolean;
  zipId?: string;
  zipName?: string;
};

const EXPERT_KEYS: Array<{ key: keyof CloudConfig; def: string; numeric?: boolean }> = [
  { key: "model", def: "gpt-5.6-terra:low" },
  { key: "subagent_model", def: "gpt-5.6-luna:low" },
  { key: "budget", def: "150000", numeric: true },
  { key: "max_iterations", def: "8", numeric: true },
  { key: "max_parallel", def: "3", numeric: true },
  { key: "language", def: "en" },
  { key: "plan", def: "false" },
  { key: "planner_model", def: "gpt-5.6-sol:medium" },
  { key: "effort_models", def: '{"high": "gpt-5.6-sol", "medium": "gpt-5.6-terra", "low": "gpt-5.6-luna"}' },
  { key: "max_plan_depth", def: "3", numeric: true },
  { key: "max_retries", def: "3", numeric: true },
  { key: "max_refinements", def: "3", numeric: true },
  { key: "critique", def: "false" },
  { key: "critique_model", def: "gpt-5.6-terra:low" },
  { key: "critique_rounds", def: "2", numeric: true },
  { key: "python", def: "true" },
  { key: "image", def: "false" },
  { key: "image_model", def: "gpt-5-mini" },
  { key: "expert_enabled", def: "false" },
];

export function Wizard() {
  const navigate = useNavigate();
  const apiUrl = useSettings((s) => s.apiUrl);
  const s3Prefix = useSettings((s) => s.s3Prefix);
  const byName = useWorkspaces((s) => s.byName);
  const upsert = useWorkspaces((s) => s.upsert);
  const sessionView = useUi((s) => s.sessionView);

  const existingAdminToken = Object.values(byName).find((w) => w.role === "admin")?.token;

  const [step, setStep] = useState(1);
  const [zipReadme, setZipReadme] = useState("");
  const [name, setName] = useState("botpilot-kurs");
  const [bootstrapToken, setBootstrapToken] = useState("");
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [pdfAsImages, setPdfAsImages] = useState(false);
  const [task, setTask] = useState(
    "Die Analyse ist fertig, wenn ",
  );
  const [budget, setBudget] = useState(150000);
  const [simpleToggles, setSimpleToggles] = useState({ plan: false, python: true, image: false, critique: false });
  const [expertValues, setExpertValues] = useState<Partial<CloudConfig>>(() => {
    const out: Partial<CloudConfig> = {};
    for (const { key, def, numeric } of EXPERT_KEYS) {
      if (def === "true" || def === "false") (out as any)[key] = def === "true";
      else if (numeric) (out as any)[key] = Number(def);
      else if (key === "effort_models") {
        try {
          (out as any)[key] = JSON.parse(def);
        } catch {
          (out as any)[key] = def;
        }
      } else (out as any)[key] = def;
    }
    return out;
  });
  const [busy, setBusy] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const isSimple = sessionView === "simple";

  async function handleZipFile(file: File) {
    try {
      const zip = await JSZip.loadAsync(file);
      let parsedReadme: string | null = null;
      let parsedConfig: any = null;
      let parsedGoal: string | null = null;
      const otherFiles: File[] = [];

      for (const [name, entry] of Object.entries(zip.files)) {
        const normalizedPath = name.replace(/\\/g, "/");
        if (entry.dir || normalizedPath.endsWith("/")) {
          console.log(`[ZIP Import] Skip directory entry: ${name}`);
          continue;
        }
        if (normalizedPath.includes("__MACOSX")) {
          console.log(`[ZIP Import] Skip macOS metadata entry: ${name}`);
          continue;
        }

        const parts = normalizedPath.split("/");
        const basename = parts[parts.length - 1];
        if (!basename) {
          console.log(`[ZIP Import] Skip empty basename entry: ${name}`);
          continue;
        }
        const cleanParts = parts.filter((p) => p && p !== "." && p !== "..");
        if (cleanParts.some((p) => p.startsWith("."))) {
          console.log(`[ZIP Import] Skip hidden dot-file/folder entry: ${name}`);
          continue;
        }

        const lowerName = basename.toLowerCase();
        if (lowerName === "readme.md") {
          console.log(`[ZIP Import] Accept special readme file: ${name}`);
          parsedReadme = await entry.async("string");
        } else if (lowerName === "config.js") {
          console.log(`[ZIP Import] Accept special config file: ${name}`);
          const content = await entry.async("string");
          parsedConfig = parseConfigJs(content);
        } else if (lowerName === "goal.md") {
          console.log(`[ZIP Import] Accept special task goal file: ${name}`);
          parsedGoal = await entry.async("string");
        } else {
          console.log(`[ZIP Import] Accept regular workspace file: ${name} -> ${basename}`);
          const blob = await entry.async("blob");
          const otherFile = new File([blob], basename, { type: blob.type });
          otherFiles.push(otherFile);
        }
      }

      if (parsedReadme !== null) {
        setZipReadme(parsedReadme);
      }
      if (parsedGoal !== null) {
        setTask(parsedGoal);
      }
      if (parsedConfig) {
        setExpertValues((prev) => ({ ...prev, ...parsedConfig }));
        if (typeof parsedConfig.budget === "number") setBudget(parsedConfig.budget);
        setSimpleToggles((prev) => ({
          plan: parsedConfig.plan ?? prev.plan,
          python: parsedConfig.python ?? prev.python,
          image: parsedConfig.image ?? prev.image,
          critique: parsedConfig.critique ?? prev.critique,
        }));
      }

      if (otherFiles.length > 0) {
        addFiles(otherFiles);
      }
      toast("ZIP importiert", `Erfolgreich importiert aus ${file.name}`, true);
    } catch (err: any) {
      toast(texte.errors.generic, `Fehler beim Entpacken der ZIP: ${err.message || err}`);
    }
  }
  async function handleSpecialFile(file: File) {
    const lowerName = file.name.toLowerCase();
    try {
      if (lowerName === "readme.md") {
        const content = await file.text();
        setZipReadme(content);
        toast("Readme geladen", `Anleitung aus ${file.name} übernommen`, true);
      } else if (lowerName === "config.js") {
        const content = await file.text();
        const parsedConfig = parseConfigJs(content);
        if (parsedConfig) {
          setExpertValues((prev) => ({ ...prev, ...parsedConfig }));
          if (typeof parsedConfig.budget === "number") setBudget(parsedConfig.budget);
          setSimpleToggles((prev) => ({
            plan: parsedConfig.plan ?? prev.plan,
            python: parsedConfig.python ?? prev.python,
            image: parsedConfig.image ?? prev.image,
            critique: parsedConfig.critique ?? prev.critique,
          }));
          toast("Konfiguration geladen", `Einstellungen aus ${file.name} übernommen`, true);
        }
      } else if (lowerName === "goal.md") {
        const content = await file.text();
        setTask(content);
        toast("Aufgabe geladen", `Aufgabentext aus ${file.name} übernommen`, true);
      }
    } catch (err: any) {
      toast(texte.errors.generic, `Fehler beim Lesen der Spezialdatei ${file.name}: ${err.message || err}`);
    }
  }

  function addFiles(list: FileList | File[]) {
    const arr = Array.from(list).map(file => {
      const normalizedName = normalizeUmlauts(file.name);
      if (normalizedName !== file.name) {
        return new File([file], normalizedName, { type: file.type });
      }
      return file;
    });
    const zips = arr.filter((f) => f.name.toLowerCase().endsWith(".zip"));
    const specialNames = ["readme.md", "config.js", "goal.md"];
    const specialFiles = arr.filter((f) => specialNames.includes(f.name.toLowerCase()));
    const regularFiles = arr.filter((f) => !f.name.toLowerCase().endsWith(".zip") && !specialNames.includes(f.name.toLowerCase()));

    if (zips.length > 0) {
      zips.forEach((zip) => handleZipFile(zip));
    }

    if (specialFiles.length > 0) {
      specialFiles.forEach((f) => handleSpecialFile(f));
    }

    if (regularFiles.length > 0) {
      setFiles((prev) => [
        ...prev,
        ...regularFiles.map((file) => ({
          id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`,
          name: file.name,
          convLabel: convLabelFor(file, pdfAsImages),
          file,
        })),
      ]);
    }
  }

  function removeFile(id: string) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function buildConfig(): CloudConfig {
    if (isSimple) {
      return {
        budget,
        plan: simpleToggles.plan,
        python: simpleToggles.python,
        image: simpleToggles.image,
        critique: simpleToggles.critique,
      };
    }
    return expertValues as CloudConfig;
  }

  async function finish() {
    const cleanName = name.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-") || "arbeitsbereich";
    const finalName = byName[cleanName] ? `${cleanName}-2` : cleanName;
    const token = existingAdminToken || bootstrapToken.trim();
    if (!token) {
      toast(texte.errors.generic, "Bitte zunächst ein Zugangstoken eingeben.");
      return;
    }
    const ctx: ApiCtx = { apiUrl, token, workspace: finalName };
    const config = buildConfig();
    console.info("[Wizard] finish", { workspace: finalName, fileCount: files.length, config });

    setBusy("Dateien werden hochgeladen…");
    try {
      const uploadableFiles = files.filter(
        (f) => !["readme.md", "config.js", "goal.md"].includes(f.name.toLowerCase())
      );
      const { smallDocs, smallImages, largeFiles } = await convertFilesForUpload(
        uploadableFiles.map((pf) => pf.file),
        pdfAsImages,
      );
      console.info("[Wizard] converted files", {
        smallDocs: smallDocs.length,
        smallImages: smallImages.length,
        largeFiles: largeFiles.length,
      });

      const { errors } = await uploadInBatches(ctx, smallDocs, smallImages, (p) =>
        setBusy(`Batch ${p.batch}/${p.totalBatches} · ${p.count} Dokumente…`),
      );
      for (const file of largeFiles) {
        setBusy(`Direkt-Upload: ${file.name}…`);
        await uploadLargeFile(s3Prefix, finalName, file);
      }
      if (errors.length) {
        console.error("[Wizard] upload errors", errors);
        toast(texte.errors.generic, `${errors.length} Dokument(e) fehlgeschlagen: ${errors.map((e) => e.doc_id).join(", ")}`);
      } else {
        console.info("[Wizard] upload complete", { written: smallDocs.length + smallImages.length + largeFiles.length });
      }

      // Legt den Arbeitsbereich an (Konfig + Aufgabe als goal.md) OHNE einen Lauf zu
      // starten — "Starten" bzw. "Plan erstellen" ist ein separater, späterer Schritt
      // auf der Workspace-Seite (dort auch für Lernende in der Einfach-Sicht erreichbar).
      setBusy("Arbeitsbereich wird angelegt…");
      const res = await api.create(ctx, task, config);
      console.info("[Wizard] create result", res);
      if (res.code < 0) {
        console.error("[Wizard] create failed", res.msg);
        toast(texte.errors.generic, res.msg ?? texte.errors.generic);
        setBusy(null);
        return;
      }
      if (zipReadme) {
        setBusy("Readme wird gespeichert…");
        await api.saveReadme(ctx, zipReadme, "admin");
      }
      upsert({
        workspace: finalName,
        token,
        role: "admin",
        view: sessionView,
        origin: "created",
        addedAt: Date.now(),
        notifiedQids: [],
        expertEnabled: false,
      });
      navigate(`/ws/${token}/${encodeURIComponent(finalName)}`);
    } catch (e) {
      console.error("[Wizard] finish failed", e);
      toast(texte.errors.generic, e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const steps = texte.wizard.steps;

  return (
    <main className="page">
      <div className="wizard">
        <div className="page-head">
          <h1>{texte.wizard.title}</h1>
        </div>
        <div className="steps">
          {steps.map((label, i) => (
            <div key={label} className={`step ${step === i + 1 ? "on" : step > i + 1 ? "done" : ""}`}>
              <div className="bar" />
              {i + 1}. {label}
            </div>
          ))}
        </div>
        <div className="card">
          {step === 1 && (
            <>
              <h2>{texte.wizard.step1Title}</h2>
              <p className="sub">{texte.wizard.step1Sub}</p>
              {!existingAdminToken && (
                <div className="field">
                  <span className="label">Zugangstoken (einmalig für diesen Browser)</span>
                  <input
                    type="text"
                    value={bootstrapToken}
                    onChange={(e) => setBootstrapToken(e.target.value)}
                    placeholder="anna_ab12cd34ef_1752300000"
                    style={{ fontFamily: "var(--mono)" }}
                  />
                </div>
              )}
              <div className="field">
                <span className="label">{texte.wizard.workspaceNameLabel}</span>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field">
                <span className="label">{texte.wizard.filesLabel}</span>
                <div
                  className={`dropzone${dragOver ? " over" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
                  }}
                >
                  {texte.wizard.dropzoneText}
                  <br />
                  <span className="hint">{texte.wizard.dropzoneHint}</span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => e.target.files && addFiles(e.target.files)}
                />
                <label className="toggle-row" style={{ marginTop: 10 }}>
                  <span className="txt">
                    <b>PDFs als Bildseiten verarbeiten</b>
                    <span className="hint">Statt Textextraktion: ein PNG pro Seite (für Bilderkennungs-Aufgaben).</span>
                  </span>
                  <span className="switch">
                    <input type="checkbox" checked={pdfAsImages} onChange={(e) => setPdfAsImages(e.target.checked)} />
                    <span className="tr" />
                  </span>
                </label>
                <div className="chips">
                  {files.map((f) => (
                    <span className="chip" key={f.id}>
                      📄 {f.name} <span className="conv">→ {f.convLabel}</span>
                      <button aria-label={texte.wizard.remove} onClick={() => removeFile(f.id)}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2>{texte.wizard.stepReadmeTitle}</h2>
              <p className="sub">{texte.wizard.stepReadmeSub}</p>
              <div className="field">
                <span className="label">{texte.wizard.readmeLabel}</span>
                <textarea value={zipReadme} onChange={(e) => setZipReadme(e.target.value)} />
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2>{texte.wizard.step2Title}</h2>
              <p className="sub">{texte.wizard.step2Sub}</p>
              <GoalEditor value={task} onChange={setTask} />
            </>
          )}

          {step === 4 &&
            (busy ? (
              <div className="upload-progress">
                <h2>{busy}</h2>
                <p className="sub">Folgende Dateien werden übertragen:</p>
                <div className="chips" style={{ marginTop: 15 }}>
                  {files.map((f) => (
                    <span className="chip" key={f.id} style={{ opacity: 0.85 }}>
                      📄 {f.name} <span className="conv">→ {f.convLabel}</span>
                    </span>
                  ))}
                </div>
              </div>
            ) : isSimple ? (
              <>
                <h2>{texte.wizard.step3TitleSimple}</h2>
                <p className="sub">{texte.wizard.step3SubSimple}</p>
                <div className="field">
                  <span className="label">{texte.wizard.budgetLabel}</span>
                  <div className="range-row">
                    <input
                      type="range"
                      min={50000}
                      max={500000}
                      step={10000}
                      value={budget}
                      onChange={(e) => setBudget(Number(e.target.value))}
                    />
                    <output className="num">{budget.toLocaleString("de-DE")} Tokens</output>
                  </div>
                  <span className="hint">{texte.wizard.budgetHint}</span>
                </div>
                {texte.wizard.toggles.map((t) => (
                  <div className="toggle-row" key={t.key}>
                    <span className="txt">
                      <b>{t.label}</b>
                      <span className="hint">{t.hint}</span>
                    </span>
                    <span className="switch">
                      <input
                        type="checkbox"
                        checked={simpleToggles[t.key as keyof typeof simpleToggles]}
                        onChange={(e) =>
                          setSimpleToggles((prev) => ({ ...prev, [t.key]: e.target.checked }))
                        }
                        aria-label={t.label}
                      />
                      <span className="tr" />
                    </span>
                  </div>
                ))}
              </>
            ) : (
              <>
                <h2>{texte.wizard.step3TitleExpert}</h2>
                <p className="sub">{texte.wizard.step3SubExpert}</p>
                <ConfigEditor draft={expertValues} setDraft={setExpertValues as any} />
              </>
            ))}

          <div className="nav">
            <button className="btn ghost" onClick={() => (step === 1 ? navigate("/") : setStep((s) => s - 1))}>
              {step === 1 ? texte.wizard.cancel : texte.wizard.back}
            </button>
            {step < 4 ? (
              <button className="btn primary" onClick={() => setStep((s) => s + 1)}>
                {texte.wizard.next}
              </button>
            ) : (
              <button className="btn primary" disabled={!!busy} onClick={finish}>
                {busy ?? texte.wizard.finishCreate}
              </button>
            )}
          </div>
          {step === 4 && !busy && <p className="hint" style={{ marginTop: 10 }}>{texte.wizard.finishHint}</p>}
        </div>
      </div>
    </main>
  );
}
